/**
 * Copyright © 2022-2024 Delicious AI, LLC
 *
 * @author Stockton Jenkins <stockton.jenkins@deliciousai.com>
 */

import { startCase } from 'lodash'
import pluralize from 'pluralize'

const titleCase = (string: string) =>
  string.replace(/(?:^|\s|-|_)\S/g, a => {
    return a.toUpperCase()
  })

const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/

const isUUID = (string: string) => uuidRegex.test(string)

const plurality = (word: string, count: number, ignore?: string[]): string => {
  if (ignore && ignore.includes(word.toLowerCase())) return word
  return pluralize(word, count)
}

const truncateText = (input: string, charLimit: number) => {
  if (input.length > charLimit) {
    input = input.slice(0, charLimit - 3).concat('...')
  }
  return input
}

const isNullOrEmpty = (str: string | null | undefined): boolean => {
  return str === null || str === undefined || str.length <= 0
}

const enumValue = (description: string) => {
  return description
    .split(' ')
    .map(word => word.toUpperCase())
    .join('_')
}

const stringValue = (enumValue: string, separator: string = '_') => {
  return titleCase(
    enumValue
      .split(separator)
      .map(word => word.toLowerCase())
      .join(' '),
  )
}

const camelCaseToTitleCase = (word: string) => startCase(word)

const snakeCaseToTitleCase = (value: string): string =>
  value
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const commaList = (
  items: (string | JSX.Element)[],
  conjunction: string,
): (string | JSX.Element)[] => {
  const length = items.length
  if (length === 1) {
    return items
  }
  if (length === 2) {
    return [items[0], ` ${conjunction} `, items[1]]
  }
  return items.reduce<Array<string | JSX.Element>>((prevArray, item, index) => {
    if (index === length - 1) {
      return [...prevArray, item]
    } else {
      const add = index === length - 2 ? `, ${conjunction} ` : ', '
      return [...prevArray, item, add]
    }
  }, [])
}

// eslint-disable-next-line no-control-regex
const clean = (string: string) => string.replace(/\x00/g, '')

const formatCurrency = (amount: number) => {
  const isNegative = amount < 0
  const absAmount = Math.abs(amount / 100).toFixed(2)
  return isNegative ? `-$${absAmount}` : `$${absAmount}`
}

export const StringHelpers = {
  titleCase,
  isUUID,
  plurality,
  truncateText,
  isNullOrEmpty,
  enumValue,
  stringValue,
  camelCaseToTitleCase,
  snakeCaseToTitleCase,
  commaList,
  clean,
  formatCurrency,
}
